import React from 'react';
import EditIcon from '@mui/icons-material/Edit';
import { useShowContext } from 'framework/show';
import useVendorService from '../../useVendorService';
import { VendorDto } from '../../types';
import { AGREEMENT_TYPES, VENDOR_DURACTION_TYPE } from '../../constants';
import EditAgreementForm from '../../createupdate/Create/EditAgreementForm';
import AgreementActionButton from './AgreementActionButton';
import { useAgreementPermissions, AGREEMENT_PERMISSIONS } from './AgreementPermissionsContext';

interface EditAgreementButtonProps {
  vendorData?: VendorDto;
}

const EditAgreementButton = ({ vendorData }: EditAgreementButtonProps) => {
  const { updateAgreement } = useVendorService();
  const { data: agreement } = useShowContext();
  const { hasPermission } = useAgreementPermissions();

  const canEdit =
    agreement?.status !== 'EXPIRED' &&
    agreement?.status !== 'TERMINATED' &&
    hasPermission(AGREEMENT_PERMISSIONS.UPDATE);

  const agreementType = agreement?.agreementType;

  const getValidationSchema = () =>
    Promise.resolve({
      legalEntity: { type: 'enum', required: true },
      agreementType: { type: 'enum', required: true },
      durationType: { type: 'enum', required: true },
      startDate: { type: 'date', required: true },
      endDate: {
        type: 'date',
        required: {
          value: true,
          when: {
            $durationType: { in: [VENDOR_DURACTION_TYPE.ONE_TIME, VENDOR_DURACTION_TYPE.FIXED_TERM] },
          },
        },
      },
      publicOfferLink: {
        type: 'string',
        required: {
          value: true,
          when: {
            $agreementType: { eq: AGREEMENT_TYPES.PUBLIC_OFFER },
          },
        },
      },
      signedContractAttachment: {
        type: 'array',
        required: {
          value: true,
          when: {
            $agreementType: { eq: AGREEMENT_TYPES.CONTRACT },
          },
        },
        maxSize: 1,
      },
    });

  const handleSubmit = async (agreementId: string, values: any) => {
    return updateAgreement(agreementId, values);
  };

  const initialData = agreement
    ? {
        legalEntity: agreement.legalEntity || '',
        agreementType: agreement.agreementType || '',
        durationType: agreement.durationType || '',
        startDate: agreement.startDate || '',
        endDate: agreement.endDate || '',
        publicOfferLink: agreement.publicOfferLink || '',
        signedContractAttachment: agreement.signedContractAttachment ? [agreement.signedContractAttachment] : [],
      }
    : {};

  return (
    <AgreementActionButton
      vendorData={vendorData}
      icon={<EditIcon />}
      color="success"
      formTitle="Edit agreement"
      tooltip="Edit agreement"
      canPerformAction={canEdit}
      onSubmit={handleSubmit}
      getValidationSchema={getValidationSchema}
      initialData={initialData}
    >
      <EditAgreementForm agreementType={agreementType} />
    </AgreementActionButton>
  );
};

export default EditAgreementButton;
